// Per-worker build drift indicator.
//
// Compares the host's `installedBuilds` against `latestBuilds` (both
// shipped on the FleetSnapshot, keyed by Chromium major) and renders:
//   - `latest`      — every reported major has its latest version installed
//   - `behind N`    — N majors are missing the latest reported build
//   - `no data`     — the worker hasn't reported latest builds yet
//
// Tooltip lists the per-major delta so operators can decide whether to
// trigger an install from the per-host page.

import { Badge } from "@veil/ui/components/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";

interface Props {
	installedBuilds: Record<string, string[]> | null | undefined;
	latestBuilds: Record<string, string> | null | undefined;
}

interface MajorDrift {
	major: string;
	latest: string;
	installed: string[];
	behind: boolean;
}

function computeDrift(
	installed: Record<string, string[]> | null | undefined,
	latest: Record<string, string> | null | undefined,
): MajorDrift[] {
	if (!latest) return [];
	const installedMap = installed ?? {};
	return Object.entries(latest)
		.map(([major, latestVer]) => {
			const versions = installedMap[major] ?? [];
			return {
				major,
				latest: latestVer,
				installed: versions,
				behind: !versions.includes(latestVer),
			};
		})
		.sort((a, b) => Number(b.major) - Number(a.major));
}

export function DriftBadge({ installedBuilds, latestBuilds }: Props) {
	const drift = computeDrift(installedBuilds, latestBuilds);

	if (drift.length === 0) {
		return (
			<Badge variant="outline" className="text-[10px] text-muted-foreground">
				no data
			</Badge>
		);
	}

	const behind = drift.filter((d) => d.behind);

	const trigger =
		behind.length === 0 ? (
			<Badge variant="success" className="text-[10px]">
				latest
			</Badge>
		) : (
			<Badge
				variant="outline"
				className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-400"
			>
				behind {behind.length}
			</Badge>
		);

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<span className="inline-flex cursor-default">{trigger}</span>
			</TooltipTrigger>
			<TooltipContent side="bottom" className="text-[11px]">
				<div className="space-y-1.5">
					<div className="font-medium">Chromium builds</div>
					<div className="space-y-1 font-mono tabular-nums">
						{drift.map((d) => (
							<div key={d.major} className="flex items-baseline gap-2">
								<span className="w-7 text-muted">{d.major}</span>
								<span className={d.behind ? "text-amber-300" : ""}>
									{d.installed.length > 0 ? d.installed.join(", ") : "—"}
								</span>
								{d.behind && (
									<>
										<span className="text-muted">→</span>
										<span className="text-background">{d.latest}</span>
									</>
								)}
							</div>
						))}
					</div>
				</div>
			</TooltipContent>
		</Tooltip>
	);
}

export function countBehindHosts(
	hosts: Array<{
		snapshot: {
			installedBuilds: Record<string, string[]> | null;
			latestBuilds: Record<string, string> | null;
		} | null;
	}>,
): number {
	let count = 0;
	for (const h of hosts) {
		if (!h.snapshot) continue;
		const drift = computeDrift(
			h.snapshot.installedBuilds,
			h.snapshot.latestBuilds,
		);
		if (drift.length > 0 && drift.some((d) => d.behind)) {
			count += 1;
		}
	}
	return count;
}
